import {jsx} from 'panel';
import moment from 'moment';
import type {Moment} from 'moment';

import {ReportName} from 'common/report/constants';
import {DashboardOrigin, getDashboardParams} from 'common/report/util/bookmark-overrides';
import {encodeRison, serializeJsUrl} from 'common/report/util/url';
import {SvgIconEl} from 'common/components/svg-icon';
import {BoardsDashboardSelector} from 'common/widgets/dashboard-selector/presets';
import {SectionItem} from 'common/components/items-menu/types';
import {Directions} from 'common/components/drop-menu/types';
import {ProjectSelectorEl} from 'common/widgets/project-selector';
import {TooltipAttrs} from 'common/components/tooltip/types';

import type {DashboardSelectorController} from '../controllers/dashboard-selector-controller';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {getProjectId, getReportUrl, getWorkspaceId} from './index';

export const LINKED_COPY_LABEL = `Create Linked Copy`;
export const DUPLICATE_LABEL = `Duplicate`;

export enum PlaceholderCopy {
  DuplicateReport = `Add report to another board...`,
  MoveReport = `Move report to another board...`,
}

export enum SaveDashboardCopy {
  DuplicateReport = `Add to New Board`,
  MoveReport = `Move to New Board`,
}

export const REPORT_ICONS = {
  [ReportName.Insights]: `brand-insights`,
  [ReportName.Funnels]: `brand-funnels`,
  [ReportName.Flows]: `brand-flows`,
  [ReportName.Retention]: `brand-retention`,
  [ReportName.Dashboards]: `brand-dashboard`,
};

export const LinkedReportIcons = {
  [ReportName.Insights]: `brand-insights-linked`,
  [ReportName.Funnels]: `brand-funnels-linked`,
  [ReportName.Flows]: `brand-flows-linked`,
  [ReportName.Retention]: `brand-retention-linked`,
};

export function getReportIcon(type: BookmarkType, isLinked = false) {
  return isLinked ? LinkedReportIcons[type] : REPORT_ICONS[type];
}

export function getDeleteReportModalAttrs() {
  return {
    'confirm-button-label': `Delete and go to boards`,
    closeable: true,
    title: `Delete Report`,
    description: `Deleting is permanent. Doing so will not delete any related reports. Are you sure you want to delete?`,
    elref: `entityDeleteConfirm`,
  };
}

export function getCardInDashboardHref({
  dashboardId,
  bookmarkId,
  cardId,
  projectId,
  workspaceId,
}: {
  dashboardId: number;
  bookmarkId?: number;
  cardId?: number | string;
  projectId: number;
  workspaceId: number;
}) {
  const base = getReportUrl(`boards`, {
    projectId,
    workspaceId,
  });
  const hashParams = [];
  if (dashboardId) {
    hashParams.push(`id=${dashboardId}`);
  }
  if (bookmarkId) {
    hashParams.push(`focused-bookmark-id=${bookmarkId}`);
  } else if (cardId) {
    hashParams.push(`card-id=${cardId}`);
  }
  return hashParams.length ? `${base}#${hashParams.join(`&`)}` : base;
}

export function getLinkWithDashboardParams(href: string, dashboard: DashboardOrigin, {projectId, workspaceId}) {
  if (dashboard && `id` in dashboard && `title` in dashboard) {
    switch (href) {
      case ReportName.Insights:
      case ReportName.Flows:
        return getReportUrl(
          `${href}#${serializeJsUrl({
            originDashboard: getDashboardParams(dashboard, {urlEncode: true}),
          })}`,
          {projectId, workspaceId},
        );
      case ReportName.Funnels:
        return getReportUrl(
          `${href}#view/${encodeRison({
            originDashboard: getDashboardParams(dashboard, {urlEncode: true}),
          })}`,
          {projectId, workspaceId},
        );
      case ReportName.Retention:
        return getReportUrl(
          `${href}#${encodeRison({
            originDashboard: getDashboardParams(dashboard, {urlEncode: true}),
          })}`,
          {projectId, workspaceId},
        );
    }
  }
  return href;
}

export function getDashboardAllowedReports(): ReportName[] {
  return [ReportName.Insights, ReportName.Funnels, ReportName.Flows, ReportName.Retention];
}

/**
 * Returns a SectionItem object compatible with the items-menu component
 */
export function getBoardsDashboardSectionItem({
  label,
  name,
  recentDashboardIds,
  placeholder,
  quickCreateSaveDashboardCopy,
  dashboardController,
  icon,
  direction,
  showProjectSelector,
  showMoveOutButton,
  hideDashboardList,
  excludedDashboards,
  currentDashboardHierarchyTreeHeight,
  descendantDashboardIds,
  tooltip,
  enableNestedDashboardsRestrictions,
}: {
  label: string;
  name: string;
  recentDashboardIds: number[];
  placeholder: string;
  quickCreateSaveDashboardCopy: string;
  dashboardController: DashboardSelectorController;
  enableNestedDashboardsRestrictions: boolean;
  icon?: string;
  direction?: Directions;
  showProjectSelector?: boolean;
  showMoveOutButton?: boolean;
  hideDashboardList?: boolean;
  excludedDashboards?: number[];
  currentDashboardHierarchyTreeHeight?: number;
  descendantDashboardIds?: Set<number>;
  tooltip?: TooltipAttrs;
}): SectionItem {
  direction ||= Directions.Right;
  return {
    label,
    name,
    icon,
    tooltip,
    actionsRenderer: () => <SvgIconEl attrs={{icon: `caret-right`}} />,
    subMenuRenderer: (onValueSelected) => (
      <BoardsDashboardSelector
        recentDashboardIds={recentDashboardIds}
        placeholder={placeholder}
        quickCreateSaveDashboardCopy={quickCreateSaveDashboardCopy}
        dashboardController={dashboardController}
        handleDashboardSelected={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onValueSelected(ev.detail.id, ev.detail);
        }}
        offset={2}
        direction={direction}
        showProjectSelector={showProjectSelector}
        hideDashboardList={hideDashboardList}
        showMoveOutButton={showMoveOutButton}
        enableNestedDashboardsRestrictions={enableNestedDashboardsRestrictions}
        excludedDashboards={excludedDashboards}
        currentDashboardHierarchyTreeHeight={currentDashboardHierarchyTreeHeight}
        descendantDashboardIds={descendantDashboardIds}
      />
    ),
  };
}

export function getProjectSelectorSectionItem({
  label,
  name,
  placeholder,
  quickCreateSaveDashboardCopy,
  icon,
  direction,
}: {
  label: string;
  name: string;
  placeholder: string;
  quickCreateSaveDashboardCopy: string;
  icon?: string;
  direction?: Directions;
}): SectionItem {
  direction ||= Directions.Right;
  return {
    label,
    name,
    icon,
    actionsRenderer: () => <SvgIconEl attrs={{icon: `caret-right`}} />,
    subMenuRenderer: (onValueSelected) => (
      <ProjectSelectorEl
        params={{searchPlaceholder: placeholder, submitCopy: quickCreateSaveDashboardCopy, direction, offset: 2}}
        on={{
          select: (ev: CustomEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            onValueSelected(undefined, ev.detail);
          },
        }}
      />
    ),
  };
}

export function getFullReportUrl(appName: string, hash: string) {
  const path = getReportUrl(`${appName}#${hash}`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
  return `${window.location.protocol}//${window.location.host}${path}`;
}

export function getLastRefreshedTimeLabel(lastUpdatedTime: Moment) {
  const now = moment();
  let timeAgoLabel = ``;
  if (now.diff(lastUpdatedTime, `minutes`) < 1) {
    timeAgoLabel = `< 1 min ago`;
  } else if (now.diff(lastUpdatedTime, `minutes`) < 60) {
    timeAgoLabel = `${now.diff(lastUpdatedTime, `minutes`)} min ago`;
  } else {
    timeAgoLabel = lastUpdatedTime.fromNow();
  }

  return `Data from ${timeAgoLabel}`;
}
